import { DynamicFormDefinition } from "./dynamic-form-definition";
import { FormDefinitionsGetRequest, FormSubmission, Patient, Provider, Schedule } from "./interfaces";

/** Forms API for sending and receiving form definitions and submissions */
export class FormsApi {

    private _url: string;
    private _clientId: string;
    private _apiKey: string | undefined;
    private _tokenCallback: (() => Promise<string>) | undefined;

    constructor(url: string, clientId: string, apiKey: string | undefined, tokenCallback: (() => Promise<string>) | undefined = undefined) {
        this._url = url;
        this._clientId = clientId;
        this._apiKey = apiKey;
        this._tokenCallback = tokenCallback;
    }

    setTokenCallback = (tokenCallback: () => Promise<string>) => {
        this._tokenCallback = tokenCallback;
    }

    get = async<T>(path: string): Promise<T> => {
        const response = await fetch(`${this._url}/${path}`, await this.buildRequestOptions());
        const json = await response.json();
        return json as T;
    };

    post = async<T, T1>(path: string, request: T): Promise<T1> => {
        const options = await this.buildRequestOptions();
        options.method = 'POST';
        options.body = JSON.stringify(request);

        const response = await fetch(`${this._url}${path}`, options);

        const json = await response.json();

        if (!response.ok) {
            throw json;
        }

        return json as T1;
    }

    fetchFormDefinitions = async (request: FormDefinitionsGetRequest | null = null): Promise<DynamicFormDefinition[]> => {
        const params = request ?? {};
        const url = this.buildUrlWithParams(`${this._url}/FormDefinitions`, params);

        const response = await fetch(url, await this.buildRequestOptions());
        const json = await response.json();
        return json as DynamicFormDefinition[];
    };

    fetchSchedules = async (): Promise<Schedule[]> => {
        const response = await fetch(`${this._url}/Schedules`, await this.buildRequestOptions());
        const json = await response.json();
        return json as Schedule[];
    };

    fetchFormDefinition = async (id: string): Promise<DynamicFormDefinition> => {
        const response = await fetch(`${this._url}/FormDefinition?id=${id}`, await this.buildRequestOptions());
        const json = await response.json();
        return json as DynamicFormDefinition;
    };

    fetchFormDefinitionRevision = async (id: string): Promise<number> => {
        const response = await fetch(`${this._url}/Revision?id=${id}`, await this.buildRequestOptions());
        const json = await response.json();
        return json as number;
    };

    fetchDocument = async (fileName: string): Promise<Blob> => {
        const response = await fetch(`${this._url}/document?fileName=${fileName}`, await this.buildRequestOptions());
        const blob = await response.blob();
        return blob;
    };

    submitForm = async (submission: FormSubmission): Promise<string> => {
        return await this.doFormSubmit(submission, '/FormSubmissions');
    };

    submitOfflineForm = async (submission: FormSubmission): Promise<string> => {
        return await this.doFormSubmit(submission, '/OfflineFormSubmissions');
    };

    private async doFormSubmit (submission: FormSubmission, path: string): Promise<string> {
        const options = await this.buildRequestOptions();
        options.method = 'POST';
        options.body = JSON.stringify(submission);

        if (options.headers != null) {
            (options.headers as any)['Accept'] = 'application/json';
            (options.headers as any)['Content-Type'] = 'application/json';
        }

        const response = await fetch(`${this._url}${path}`, options);

        const json = await response.json();

        if (!response.ok) {
            throw json;
        }

        return json as string;
    };

    uploadFiles = async (formSubmissionId: string, files: File[], recaptchaToken: string): Promise<void> => {
        const formData = new FormData();
        formData.append('formSubmissionId', formSubmissionId);
        formData.append('recaptchaToken', recaptchaToken);

        for (var i = 0; i < files.length; i++) {
            formData.append('file', files[i], files[i].name);
        }

        var options = await this.buildRequestOptions();
        options.method = 'POST';
        options.body = formData;

        const response = await fetch(`${this._url}/fileUpload`, options);

        if (!response.ok) {
            throw response.statusText;
        }
    };

    fetchProviders = async (): Promise<Provider[]> => {
        const response = await fetch(`${this._url}/Providers`, await this.buildRequestOptions());
        const json = await response.json();
        return json as Provider[];
    };

    fetchPatients = async (): Promise<Patient[]> => {
        const response = await fetch(`${this._url}/Patients`, await this.buildRequestOptions());
        const json = await response.json();
        return json as Patient[];
    };

    savePatient = async (patient: Patient): Promise<string> => {
        var options = await this.buildRequestOptions();
        options.method = 'POST';
        options.body = JSON.stringify(patient);

        var response = await fetch(`${this._url}/savePatient`, options);
        const json = await response.json();
        return json as string;
    };

    deletePatient = async (id: string): Promise<void> => {
        var options = await this.buildRequestOptions();
        options.method = 'DELETE';

        var response = await fetch(`${this._url}/deletePatient?id=${id}`, options);
    };

    private async buildRequestOptions(): Promise<RequestInit> {
        var options = {
            headers: new Headers({
                'Ocp-Apim-Subscription-Key': this._apiKey ?? ''
            })
        };

        if (this._clientId) {
            options.headers.set('Client-Id', this._clientId);
        }

        if (this._tokenCallback) {
            var token = await this._tokenCallback();

            options.headers.set('Authorization', `Bearer ${token}`);
        }

        return options;
    };

    private buildUrlWithParams(url: string, params: any): URL {
        const urlObject = new URL(url);
        Object.keys(params).forEach(key => urlObject.searchParams.append(key, params[key]));

        return urlObject;
    }
}